'use client';

import * as React from 'react';
import { useMemo, useState, memo, useEffect } from 'react';
import dynamic from 'next/dynamic';
import SearchModal from '../search_modal';
import SearchPill from './SearchPill';

// Define the props type for our map component to satisfy TypeScript
interface NYCMapProps {
    venues: Array<{
        id: string;
        name: string;
        lat: number;
        lng: number;
        price: number;
        hasNoMinSpend: boolean;
    }>;
    center?: [number, number];
    zoom?: number;
}

// Memoize SearchPill and other components to prevent unnecessary re-renders
const MemoizedSearchPill = memo(SearchPill);

// Use dynamic import with preload for components not needed immediately
const DynamicSearchModal = dynamic(() => import('../search_modal'), {
    ssr: false,
    loading: () => null
});

const HeroSection = () => {
    const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
    const [hasScrolled, setHasScrolled] = useState(false);

    // Add scroll event listener
    useEffect(() => {
        const handleScroll = () => {
            // Show button after scrolling 100px
            setHasScrolled(window.scrollY > 100);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Prepare a set of sample venues for the map demo
    // This is lightweight data that doesn't need to be fetched
    const venueData = useMemo(() => Array(8).fill(null).map((_, idx) => ({
        id: `sample-${idx}`,
        name: `Venue ${idx + 1}`,
        lat: 40.7431 + (Math.random() - 0.5) * 0.05,
        lng: -73.98 + (Math.random() - 0.5) * 0.05,
        price: Math.floor(Math.random() * 200) + 100,
        hasNoMinSpend: Math.random() > 0.5
    })), []);

    // Default values for search - memoized to prevent recreation on each render
    const defaultValues = useMemo(() => {
        const nextWeek = new Date();
        nextWeek.setDate(nextWeek.getDate() + 7);

        return {
            date: nextWeek,
            time: '19:00',
            partySize: '25',
            location: '',
            eventType: '',
        };
    }, []);

    const { date, time, partySize, location, eventType } = defaultValues;

    const openSearchModal = () => {
        setIsSearchModalOpen(true);
    };

    const closeSearchModal = () => {
        setIsSearchModalOpen(false);
    };

    return (
        <section className="relative min-h-[90vh] overflow-hidden" aria-labelledby="main-heading">
            {/* CSS for mobile button hiding */}
            <style jsx>{`
                @media (min-width: 768px) {
                    .mobile-search-button {
                        display: none !important;
                    }
                }
            `}</style>

            {/* Background */}
            <div className="absolute inset-0 z-0" aria-hidden="true">
                <div className="w-full h-full bg-primary"></div>
            </div>

            <div className="container mx-auto relative z-10 px-6 md:px-10 max-w-7xl">
                {/* Content structure with repositioned search pill */}
                <div className="flex flex-col items-center">
                    {/* Top content with text and video */}
                    <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-0 md:gap-8 lg:gap-16 items-center pt-[4rem] relative">
                        {/* Text content */}
                        <div className="order-2 md:order-1 mt-0 md:mt-0 flex flex-col px-4 md:px-0">
                            {/* Main Heading - Removing heading-xl class that's overriding our size */}
                            <h1 id="main-heading" className="text-5xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-2 md:mb-4 leading-[0.85] sm:leading-[0.8] md:leading-[0.75] uppercase text-white tracking-tighter text-right md:text-right" style={{ letterSpacing: "-0.01em", wordSpacing: "-0.03em", lineHeight: "0.8 !important" }}>
                                BOOK NYC'S<br />
                                Best Venues &<br />
                                Party Spaces
                            </h1>

                            {/* Subheading - simplified for mobile */}
                            <div className="w-full mb-32 h-full">
                                <div className="text-white ml-auto text-xs md:text-base subtitle text-right md:text-right w-full md:w-auto md:max-w-[90%] lg:max-w-[80%] pr-0 md:pr-0 h-full">
                                    <p className="mb-1">Instantly Discover & Book Unique NYC Event Spaces:</p>
                                    <p className="mb-1">Find Birthday Party Venues, Private Dining,</p>
                                    <p>Rooftop Bars, Wedding Venues & More.</p>
                                </div>
                            </div>
                        </div>

                        {/* Video section - replaces phone mockup */}
                        <div className="flex justify-center md:justify-end order-1 md:order-2 relative mb-6 md:mb-0">
                            <div className="h-[70vh] md:h-[87vh] aspect-[3/4] overflow-hidden shadow-xl mt-0 pt-0">
                                <video
                                    className="object-cover w-full h-full"
                                    autoPlay
                                    muted
                                    loop
                                    playsInline
                                    aria-label="NYC venues showcase video"
                                >
                                    <source
                                        src="https://firebasestorage.googleapis.com/v0/b/rentabar-3d509.appspot.com/o/venues%2FKPUOnG8youRDUEw57iiK%2Fvideos%2Fd94e7470.webm?alt=media&token=d3809171-6c37-4867-ae18-9f0a830a3430"
                                        type="video/webm"
                                    />
                                    Your browser does not support the video tag.
                                </video>
                            </div>
                        </div>
                    </div>

                    {/* Search Pill - repositioned to overlap with video */}
                    <div className="w-full -mt-24 md:-mt-32 relative z-20">
                        {/* Desktop Search Form - Only visible on medium screens and up */}
                        <div className="hidden md:block w-full mb-12">

                            {/* Features text */}
                            <p className="text-gray-300 text-sm subtitle text-right" style={{ width: '50%' }}>
                                Amazing bars, lounges, restaurants & event venues.
                            </p>
                            <p className="text-gray-300 text-sm subtitle pb-2 text-right" style={{ width: '50%' }}>
                                Upfront pricing. Real
                                time availability. Book instantly. Party hard.
                            </p>
                            <MemoizedSearchPill
                                defaultDate={date || undefined}
                                defaultTime={time}
                                defaultPartySize={partySize}
                                defaultLocation={location}
                                defaultEventType={eventType}
                                className="shadow-xl max-w-4xl mx-auto"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* SearchModal component - only mounted when needed */}
            {isSearchModalOpen && (
                <DynamicSearchModal
                    isOpen={isSearchModalOpen}
                    onClose={closeSearchModal}
                />
            )}
            {/* Scroll-triggered mobile button - hidden on desktop */}
            <div
                className={`mobile-search-button fixed bottom-6 left-0 right-0 z-50 flex justify-center transition-opacity duration-300 ${hasScrolled ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
            >
                <button
                    onClick={openSearchModal}
                    className="bg-[#C0C0DD] text-[#1a1b23] py-3 px-8 rounded-full font-semibold text-base shadow-lg"
                >
                    SEARCH FOR A VENUE
                </button>
            </div>
        </section>
    );
};

export default memo(HeroSection); 